import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';

/**
 * Обнаружение новой версии фронта в уже открытых вкладках.
 *
 * Хешированное имя бандла (assets/index-XXXX.js) меняется при каждой сборке.
 * При возврате фокуса на вкладку (и раз в 10 минут) тянем свежий index.html
 * (no-store) и сравниваем имя бандла с загруженным. Отличается — показываем
 * уведомление с кнопкой «Обновить» (пользователи забывают Ctrl+R).
 */

const BUNDLE_RE = /assets\/index-[\w-]+\.js/;
const CHECK_INTERVAL_MS = 10 * 60_000;
const NOTIFICATION_ID = 'new-version-available';

function currentBundle(): string | null {
  for (const s of Array.from(document.scripts)) {
    const m = s.src.match(BUNDLE_RE);
    if (m) return m[0];
  }
  return null;
}

async function latestBundle(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.text()).match(BUNDLE_RE)?.[0] ?? null;
  } catch {
    return null;
  }
}

export function useVersionCheck() {
  useEffect(() => {
    const initial = currentBundle();
    if (!initial) return undefined; // dev-режим (vite) — бандла нет, проверка не нужна

    let notified = false;
    const check = async () => {
      if (notified || document.hidden) return;
      const latest = await latestBundle();
      if (latest && latest !== initial) {
        notified = true;
        notifications.show({
          id: NOTIFICATION_ID,
          title: 'Доступна новая версия',
          message: 'Приложение обновлено. Нажмите, чтобы перезагрузить страницу.',
          color: 'blue',
          autoClose: false,
          withCloseButton: false,
          onClick: () => window.location.reload(),
          style: { cursor: 'pointer' },
        });
      }
    };

    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);
}
